import type { PageBlock, SiteContent } from "@/lib/content/types";
import { DEFAULT_LOCALE, LOCALES, type LocaleSettingKey } from "./catalog";
import { LOCALE_CODE_PATTERN, nativeNameOf, englishNameOf } from "./regions";

/* Translated content.

   The UI catalog covers the app's own strings; this covers what the operator
   writes — the missions section, and what each page builder block is told
   (slides, cards, documents, text: every component's copy lives on its
   block). A translation is a whole copy of the section (or the block's
   settings) for one language, made from the English one the first time it is
   opened and edited from there, so images and texts can both differ per
   language.

   The visitor's language is a cookie as well as a stored preference, so the
   server renders the translated content on the first byte. */

export const LOCALE_COOKIE = "northstar_locale";

export const LOCALIZABLE_KEYS = ["missions"] as const;

export type LocalizableKey = (typeof LOCALIZABLE_KEYS)[number];

export const isLocalizableKey = (key: string): key is LocalizableKey =>
  (LOCALIZABLE_KEYS as readonly string[]).includes(key);

export const isLocaleCode = (value: string): boolean => LOCALE_CODE_PATTERN.test(value);

/* Every locale this site can speak: the built-ins, plus each language or
   language/country combination the operator added under Languages. */
export const siteLocales = (
  content: Pick<SiteContent, "i18n">,
): { code: string; name: string; nativeName: string }[] => {
  const builtIn = LOCALES.map((locale) => ({
    code: locale.code as string,
    name: locale.name as string,
    nativeName: locale.nativeName as string,
  }));
  const taken = new Set<string>(builtIn.map((locale) => locale.code));
  const custom = (content.i18n.custom ?? [])
    .filter((entry) => LOCALE_CODE_PATTERN.test(entry.code) && !taken.has(entry.code))
    .map((entry) => ({
      code: entry.code,
      name: englishNameOf(entry.code),
      nativeName: entry.name.trim() || nativeNameOf(entry.code),
    }));
  return [...builtIn, ...custom];
};

/* Every language switched on under Languages: the built-ins by their
   settings boolean, every operator-added combination by being on the list,
   English by its own toggle. The site's default language leads the list and
   is always on — which also means English cannot disappear while it is the
   default. */
export const enabledLocales = (content: Pick<SiteContent, "i18n">): string[] => {
  const builtIn = LOCALES.filter(
    (locale) => locale.code !== DEFAULT_LOCALE && content.i18n[locale.settingKey as LocaleSettingKey] === true,
  ).map((locale) => locale.code as string);
  const taken = new Set<string>([DEFAULT_LOCALE, ...builtIn]);
  const custom = (content.i18n.custom ?? [])
    .map((entry) => entry.code)
    .filter((code) => LOCALE_CODE_PATTERN.test(code) && !taken.has(code));
  const all = [DEFAULT_LOCALE, ...builtIn, ...custom];

  const fallbackDefault = content.i18n.defaultLocale?.trim() || DEFAULT_LOCALE;
  const siteDefault = all.includes(fallbackDefault) ? fallbackDefault : DEFAULT_LOCALE;
  const englishOff = content.i18n.en === false && siteDefault !== DEFAULT_LOCALE;
  const offered = englishOff ? all.filter((code) => code !== DEFAULT_LOCALE) : all;
  return [siteDefault, ...offered.filter((code) => code !== siteDefault)];
};

/* The language a first-time visitor lands on: the operator's choice when it
   is an enabled language, English otherwise. */
export const defaultLocaleOf = (content: Pick<SiteContent, "i18n">): string => enabledLocales(content)[0];

/* What to call a locale. The built-ins carry their names; an operator-added
   one is named by its entry (or the language's own name for itself), which
   is why callers that can pass the content get the better answer. */
export const localeName = (code: string, content?: Pick<SiteContent, "i18n">) => {
  const builtIn = LOCALES.find((locale) => locale.code === code);
  if (builtIn) return builtIn.nativeName;
  const entry = content?.i18n.custom?.find((candidate) => candidate.code === code);
  if (entry && entry.name.trim()) return entry.name.trim();
  return nativeNameOf(code);
};

const localizeBlock = (block: PageBlock, locale: string): PageBlock => {
  const props = block.locales?.[locale];

  return props ? { ...block, props } : block;
};

/* The content a visitor in `locale` sees: every translated section in place
   of its English one, every translated block setting in place of the
   English one, and English wherever no translation exists. An unknown or
   disabled language is English. */
export const applyLocale = (content: SiteContent, locale: string): SiteContent => {
  if (locale === "en" || !enabledLocales(content).includes(locale)) return content;

  const translated = content.localized?.[locale] ?? {};
  const next: SiteContent = { ...content };

  for (const key of LOCALIZABLE_KEYS) {
    const value = translated[key];
    if (value !== undefined) (next as Record<string, unknown>)[key] = value;
  }

  next.pages = content.pages.map((page) => ({
    ...page,
    blocks: page.blocks.map((block) => localizeBlock(block, locale)),
  }));

  return next;
};
